import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { useMediaQuery } from 'react-responsive';

const ExpertiseCorporative = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 300], [0, 80]);
  const y2 = useTransform(scrollY, [0, 500], [0, -120]);

  // Responsive breakpoints
  const isSmallMobile = useMediaQuery({ maxWidth: 480 });
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const isTablet = useMediaQuery({ maxWidth: 1024 });
  const isDesktop = useMediaQuery({ minWidth: 1025 });

  const getFontSize = (desktop, tablet, mobile, smallMobile) => {
    if (isSmallMobile) return smallMobile;
    if (isMobile) return mobile;
    if (isTablet) return tablet;
    return desktop;
  };

  const getSpacing = (desktop, tablet, mobile, smallMobile) => {
    if (isSmallMobile) return smallMobile;
    if (isMobile) return mobile;
    if (isTablet) return tablet;
    return desktop;
  };

  // Animations plus douces et professionnelles (Premium feel)
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } // Smooth easing
    }
  };

  const floatAnimation = {
    y: [0, -8, 0],
    transition: { duration: 4, repeat: Infinity, ease: "easeInOut" }
  };

  return (
    <>
      <Helmet>
        <title>Expertise Corporative - KMJ & Associés</title>
        <meta name="description" content="Conseil en gouvernance d'entreprise et stratégie juridique. Expertise corporative complète pour structurer votre gouvernance interne." />
      </Helmet>

      <motion.section 
        style={styles.section}
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Particles Background - Transformé en fine "poussière d'or/beige" */}
        <div style={styles.particlesContainer}>
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              style={{
                ...styles.particle,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: Math.random() * 3 + 1,
                height: Math.random() * 3 + 1,
              }}
              animate={{
                y: [0, -40, 0],
                opacity: [0.1, 0.5, 0.1],
              }}
              transition={{
                duration: Math.random() * 5 + 4,
                repeat: Infinity,
                ease: "linear",
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        {/* Animated Background Elements - Subtle Beige Glow */}
        <motion.div style={{ ...styles.glowOrb1, y: y1 }} />
        <motion.div style={{ ...styles.glowOrb2, y: y2 }} />
        
        <div style={styles.noiseOverlay} />

        <div style={styles.container}>
          {/* Hero Section */}
          <motion.div style={styles.hero} variants={itemVariants}>
            <div style={{
              ...styles.heroContent,
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: getSpacing('6rem', '4rem', '2.5rem', '2rem'),
            }}>
              
              {/* Colonne Texte */}
              <div style={styles.heroTextContainer}>
                <motion.div
                  style={styles.badge}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <span style={styles.badgeText}>Service Premium</span>
                </motion.div>
                
                <motion.div variants={itemVariants}>
                  <motion.h1 
                    style={{
                      ...styles.mainTitle,
                      fontSize: getFontSize('clamp(3rem, 5vw, 4.5rem)', '2.8rem', '2.2rem', '1.8rem'),
                      marginBottom: getSpacing('1.5rem', '1.2rem', '1rem', '0.8rem'),
                    }}
                  >
                    <span style={styles.titleGradient}>Expertise</span>
                    <br />
                    <span style={styles.titleSolid}>Corporative</span>
                  </motion.h1>
                  
                  <motion.p 
                    style={{
                      ...styles.subtitle,
                      fontSize: getFontSize('1.4rem', '1.3rem', '1.1rem', '1rem'),
                      marginBottom: getSpacing('3rem', '2.5rem', '2rem', '1.5rem'),
                    }}
                  >
                    <span style={styles.subtitleAccent}>Conseil en gouvernance</span> d'entreprise et stratégie juridique.
                  </motion.p>
                </motion.div>
                
                <motion.div style={styles.textContent} variants={itemVariants}>
                  <p style={{ ...styles.heroText, fontSize: getFontSize('1.1rem', '1.05rem', '1rem', '0.95rem') }}>
                    <span style={styles.textHighlight}>KMJ & Associés</span> propose une expertise corporative d'excellence, en accompagnant les dirigeants dans la structuration et l'optimisation de leur gouvernance interne.
                  </p>
                  
                  <p style={{ ...styles.heroText, fontSize: getFontSize('1.1rem', '1.05rem', '1rem', '0.95rem') }}>
                    Nous intervenons dans la définition des organes de décision, l'élaboration de cadres réglementaires stricts et la mise en place de procédures conformes aux plus hauts standards de gestion.
                  </p>
                  
                  <p style={{ ...styles.heroText, fontSize: getFontSize('1.1rem', '1.05rem', '1rem', '0.95rem') }}>
                    Notre cabinet sécurise vos intérêts lors de vos opérations stratégiques majeures (fusions, acquisitions, restructurations), protégeant ainsi l'ensemble de vos parties prenantes.
                  </p>
                </motion.div>

                {/* Stats */}
                <motion.div style={styles.statsContainer} variants={itemVariants}>
                  {[
                    { value: "2024", label: "Fondation" },
                    { value: "200+", label: "Dossiers" },
                    { value: "30+", label: "Associés" }
                  ].map((stat, index) => (
                    <div key={index} style={styles.statItem}>
                      <div style={styles.statValue}>{stat.value}</div>
                      <div style={styles.statLabel}>{stat.label}</div>
                    </div>
                  ))}
                </motion.div>
              </div>

              {/* Colonne Image */}
              <motion.div 
                style={styles.heroImage}
                variants={itemVariants}
              >
                <motion.div 
                  style={{
                    ...styles.imageWrapper,
                    height: isSmallMobile ? '350px' : isMobile ? '450px' : '650px',
                  }}
                  animate={floatAnimation}
                >
                  <div style={styles.imageBackground} />
                  <img
                    src="/images/L2.png" // Assurez-vous que l'image est sobre (bâtiment, bureau, avocat)
                    alt="Expertise Corporative"
                    style={styles.image}
                    loading="lazy"
                  />
                  <div style={styles.imageOverlay} />
                  
                  <motion.div 
                    style={{
                      ...styles.floatingBadge,
                      fontSize: getFontSize('1rem', '0.95rem', '0.9rem', '0.85rem'),
                    }}
                  >
                    <span style={styles.badgeIcon}>⚖️</span>
                    <span>Excellence Juridique</span>
                  </motion.div>
                </motion.div>
              </motion.div>

            </div>
          </motion.div>
        </div>
      </motion.section>
    </>
  );
};

// --- STYLES: Thème Dark Gray & Beige ---
const colors = {
  bgDark: '#121214',
  bgMedium: '#1C1C1F',
  beigeLight: '#F5EFEB',
  beigeMain: '#D4C5B0',
  beigeDark: '#A3947F',
  textGray: '#A0A0A5',
  border: 'rgba(212, 197, 176, 0.15)'
};

const styles = {
  section: {
    background: `linear-gradient(135deg, ${colors.bgDark} 0%, #171719 50%, ${colors.bgDark} 100%)`,
    color: colors.beigeLight,
    position: 'relative',
    overflow: 'hidden',
    minHeight: '100vh',
    // L'utilisation d'une police avec empattements (Serif) pour les titres donne un look très "cabinet d'avocats"
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  
  particlesContainer: {
    position: 'absolute',
    top: 0, left: 0, width: '100%', height: '100%',
    pointerEvents: 'none', zIndex: 1,
  },
  
  particle: {
    position: 'absolute',
    background: colors.beigeMain,
    borderRadius: '50%',
    filter: 'blur(1px)',
    boxShadow: `0 0 10px ${colors.beigeMain}`,
  },
  
  glowOrb1: {
    position: 'absolute',
    width: '60vw', height: '60vw',
    borderRadius: '50%',
    background: `radial-gradient(circle, rgba(212, 197, 176, 0.03) 0%, transparent 70%)`,
    top: '-20%', right: '-10%',
    filter: 'blur(60px)', zIndex: 0,
  },
  
  glowOrb2: {
    position: 'absolute',
    width: '50vw', height: '50vw',
    borderRadius: '50%',
    background: `radial-gradient(circle, rgba(212, 197, 176, 0.02) 0%, transparent 70%)`,
    bottom: '-20%', left: '-10%',
    filter: 'blur(60px)', zIndex: 0,
  },
  
  noiseOverlay: {
    position: 'absolute',
    top: 0, left: 0, width: '100%', height: '100%',
    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.04'/%3E%3C/svg%3E")`,
    pointerEvents: 'none', zIndex: 1,
  },
  
  container: {
    position: 'relative', zIndex: 2,
    maxWidth: '1440px', margin: '0 auto',
    padding: '8rem 4rem',
    '@media (max-width: 768px)': { padding: '5rem 2rem' },
  },
  
  hero: {
    position: 'relative',
  },
  
  heroContent: {
    display: 'grid',
    alignItems: 'center',
  },
  
  heroTextContainer: {
    position: 'relative', zIndex: 2,
  },
  
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.5rem 1.2rem',
    borderRadius: '4px',
    border: `1px solid ${colors.beigeDark}`,
    marginBottom: '2.5rem',
    background: 'rgba(212, 197, 176, 0.05)',
  },
  
  badgeText: {
    color: colors.beigeMain,
    fontSize: '0.85rem',
    fontWeight: '600',
    letterSpacing: '2px',
    textTransform: 'uppercase',
  },
  
  mainTitle: {
    margin: 0,
    fontWeight: '300',
    lineHeight: 1.1,
    letterSpacing: '-1px',
  },
  
  titleGradient: {
    color: colors.beigeLight,
    fontWeight: '400',
  },
  
  titleSolid: {
    color: colors.beigeMain,
    fontWeight: '600',
    fontStyle: 'italic', // Apporte une touche très éditoriale/corporate
  },
  
  subtitle: {
    color: colors.textGray,
    fontWeight: '300',
    lineHeight: 1.5,
  },
  
  subtitleAccent: {
    color: colors.beigeMain,
    fontWeight: '500',
  },
  
  textContent: {
    marginBottom: '3.5rem',
  },
  
  heroText: {
    lineHeight: 1.7,
    color: colors.textGray,
    marginBottom: '1.2rem',
    fontWeight: '300',
  },
  
  textHighlight: {
    color: colors.beigeLight,
    fontWeight: '500',
  },
  
  statsContainer: {
    display: 'flex',
    gap: '3rem',
    flexWrap: 'wrap',
    paddingTop: '2rem',
    borderTop: `1px solid ${colors.border}`,
  },
  
  statItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  
  statValue: {
    color: colors.beigeMain,
    fontSize: '2.2rem',
    fontWeight: '300',
    fontFamily: "'Playfair Display', serif", // Optionnel, donne un style classique
    marginBottom: '0.3rem',
  },
  
  statLabel: {
    color: colors.textGray,
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: '1.5px',
    fontWeight: '500',
  },
  
  heroImage: {
    position: 'relative',
    padding: '20px', // Espace pour le cadre décalé
  },
  
  imageWrapper: {
    position: 'relative',
    width: '100%',
    borderRadius: '2px',
  },
  
  // Remplacement de l'animation néon par un cadre décalé élégant
  imageBackground: {
    position: 'absolute',
    top: '20px', left: '20px', right: '-20px', bottom: '-20px',
    border: `1px solid ${colors.beigeDark}`,
    borderRadius: '2px',
    zIndex: -1,
  },
  
  image: {
    width: '100%', height: '100%',
    objectFit: 'cover',
    borderRadius: '2px',
    filter: 'contrast(1.1) brightness(0.9) grayscale(20%)', // Donne un aspect plus dramatique/premium à l'image
  },
  
  // Overlay léger pour s'assurer que l'image s'intègre bien avec le fond sombre
  imageOverlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    background: `linear-gradient(to top, rgba(18,18,20,0.6) 0%, transparent 40%)`,
  },
  
  floatingBadge: {
    position: 'absolute',
    bottom: '-15px', left: '-15px',
    background: colors.bgMedium,
    padding: '1rem 1.5rem',
    border: `1px solid ${colors.border}`,
    color: colors.beigeLight,
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '0.8rem',
    zIndex: 3,
    boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
  },
  
  badgeIcon: {
    fontSize: '1.2rem',
    color: colors.beigeMain,
  },
};

export default ExpertiseCorporative;